'use client';

import { FC, ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';

import {
  useActiveHeaderSlice,
  useCookies,
  useFeaturedFlag,
  useShoppingMode,
} from '@/hooks';
import { useAnalytics, useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { ILocalAddress, useData } from '@/components';
import { TreezStore } from '@/types';
import { CookiesNames } from '@/constants';
import {
  getDeliveryScheduleByAddress,
  getExternalStores,
  mergeStoresWithFlag,
} from '@/utils';

import { StoreLocatorContext } from './context';

import style from './storelocator.module.scss';
interface IStoreLocatorProviderProps {
  children: ReactNode;
}
const StoreLocatorProvider: FC<IStoreLocatorProviderProps> = ({ children }) => {
  const { treezStores, storeSelector, store } = useData();
  const activeSlice = useActiveHeaderSlice();
  const externalStores = getExternalStores();
  const { measureSurfsideChangeLocation } = useAnalytics();
  const { forceToSelectStore } = useFeaturedFlag();
  const { setCookieValue } = useCookies();

  const [stores, setStores] = useState<TreezStore[]>([]);
  const [pickupAllStore, setPickupAllStore] = useState<TreezStore[]>([]);
  const {
    is_crm_modal_open,
    terms_accepted,
    treez_store_name,
    delivery_details,
    delivery_address,
    temporary_fulfillment_method,
    setState,
  } = useEcomStoreSelector([
    'treez_store_name',
    'delivery_details',
    'delivery_address',
    'is_crm_modal_open',
    'terms_accepted',
    'temporary_fulfillment_method',
  ]);
  const { is_store_location_open, setState: setUiState } = useUiStoreSelector([
    'is_store_location_open',
  ]);

  const { singleStore, onlyPickup, onlyDelivery, deliveryAndPickup } =
    useShoppingMode();

  const [isDelivery, setIsDelivery] = useState<boolean>(false);
  const [isPickup, setIsPickup] = useState<boolean>(false);
  const [deliveryAddress, setDeliveryAddress] = useState<
    ILocalAddress | undefined
  >();
  const [storeAddress, setStoreAddress] = useState('');

  const [isDeliveryAddressInvalid, setIsDeliveryAddressInvalid] =
    useState(false);

  const [isPickupInvalid, setIsPickupInvalid] = useState(false);

  const [selectedStoreId, setSelectedStoreId] = useState<string>('');

  const [showOverlay, setShowOverlay] = useState(false);

  // Set cookie whenever selectedStoreId changes
  useEffect(() => {
    if (selectedStoreId)
      setCookieValue(CookiesNames._SELECTED_STORE_ID, selectedStoreId);
  }, [selectedStoreId]);

  const pickupSectionTitle =
    storeSelector?.pickup_section_title ?? 'Select a pickup location';

  const deliverySectionTitle =
    storeSelector?.delivery_section_title ?? 'Enter delivery address';

  const title = storeSelector?.title ?? 'How do you want to shop?';

  const buttonLabel = storeSelector?.button_label ?? 'How do you want to shop?';

  const mainButtonType = storeSelector?.main_button_type ?? 'primary';

  const pickupMainButtonText =
    storeSelector?.pickup_main_button_text ?? 'SHOP PICKUP';

  const deliveryMainButtonText =
    storeSelector?.delivery_main_button_text ?? 'SHOP DELIVERY';

  const secondaryButtonType =
    storeSelector?.secondary_button_type ?? 'secondary';

  const secondaryButtonText =
    storeSelector?.secondary_button_text ?? 'VIEW ALL LOCATIONS';

  const secondaryButtonLink =
    storeSelector?.secondary_button_link ?? '/store-locator';

  const hideSecondaryButton = storeSelector?.hide_secondary_button;

  const hideIconSecondaryButton = storeSelector?.hide_icon_secondary_button;

  const showSearchBox = storeSelector?.display_searchbox || false;

  useEffect(() => {
    // if the store offers pickup and delivery, select pickup by default, if it offer only
    // delivery, select delivery by default
    const storeShortName = treezStores[0].shortName;

    if (singleStore && !forceToSelectStore) {
      setState(state => {
        if (
          !state.delivery_details.pickup &&
          !state.delivery_details.delivery
        ) {
          return {
            delivery_details: {
              delivery: onlyDelivery,
              pickup: !onlyDelivery,
            },
            treez_store_name: storeShortName,
          };
        }
        return state;
      });

      measureSurfsideChangeLocation(storeShortName);
    }
  }, []);

  // this is a feature flag that forces the user
  // to select a store
  useEffect(() => {
    if (forceToSelectStore) {
      // this is a feature flag
      if (
        !treez_store_name && // not store selected
        !is_crm_modal_open && // cmr modal is not open
        terms_accepted // age gate accepted
      ) {
        setShowOverlay(true);
        setUiState({ is_store_location_open: true });
      }
    }
  }, [
    is_store_location_open,
    terms_accepted,
    is_crm_modal_open,
    forceToSelectStore,
    singleStore,
    treez_store_name,
  ]);

  useEffect(() => {
    if (!is_store_location_open) {
      setState({ temporary_fulfillment_method: null });
    }

    if (temporary_fulfillment_method) {
      setIsDelivery(temporary_fulfillment_method === 'DELIVERY');
      setIsPickup(temporary_fulfillment_method === 'PICKUP');
      return;
    }
    // pickup takes precedence over delivery when both flags on delivery_details are active
    // NOTE: if delivery_details.pickup/delivery should NOT prioritize pickup when both are active,
    // remove this block and let delivery-check execute first instead.
    if (onlyPickup || delivery_details.pickup) {
      setIsPickup(true);
      setIsDelivery(false);
      return;
    }

    if (onlyDelivery || delivery_details.delivery) {
      setIsPickup(false);
      setIsDelivery(true);
      return;
    }

    setIsPickup(true);
    setIsDelivery(false);
  }, [
    delivery_details.delivery,
    delivery_details.pickup,
    onlyDelivery,
    onlyPickup,
    is_store_location_open,
    temporary_fulfillment_method,
  ]);

  useEffect(() => {
    // by default, select the only available store
    if (
      singleStore &&
      !treez_store_name &&
      forceToSelectStore &&
      !onlyDelivery
    ) {
      const storeShortName = treezStores[0].shortName;
      setSelectedStoreId(storeShortName);
      return;
    }

    if (isDelivery && selectedStoreId) {
      return;
    }

    setSelectedStoreId(treez_store_name);
  }, [treez_store_name, is_store_location_open]);

  useEffect(() => {
    if (delivery_details?.delivery) {
      setDeliveryAddress(delivery_address as ILocalAddress);
    }
  }, [delivery_address]);

  // filter the stores based on the shopping mode and
  // always show the selected store first
  useEffect(() => {
    if (isDelivery && selectedStoreId) {
      const deliverySchelude = getDeliveryScheduleByAddress(
        deliveryAddress!,
        store,
      );

      if (deliverySchelude && !!deliverySchelude?.stores_provider_id) {
        setSelectedStoreId(deliverySchelude?.stores_provider_id);
      }
    } else if (isPickup && selectedStoreId && !!treez_store_name) {
      setSelectedStoreId(treez_store_name);
    }

    const fs = treezStores.filter(store =>
      isDelivery ? store.delivery : store.pickup,
    );

    const t = fs.find(store => store.shortName === selectedStoreId);

    if (!t) {
      setStores(fs);
      if (isPickup) {
        setPickupAllStore(mergeStoresWithFlag(fs, externalStores));
      }
      return;
    }

    const stores = [
      t,
      ...fs.filter(store => store.shortName !== selectedStoreId),
    ];

    setStores(stores);

    if (isPickup) {
      setPickupAllStore(mergeStoresWithFlag(stores, externalStores));
    }
  }, [is_store_location_open, isDelivery, isPickup]);

  return (
    <StoreLocatorContext
      value={{
        deliveryAddress: deliveryAddress as ILocalAddress,
        deliveryAndPickup,
        isDelivery,
        isDeliveryAddressInvalid,
        isPickup,
        isPickupInvalid,
        onlyDelivery,
        onlyPickup,
        selectedStoreId,
        setDeliveryAddress,
        setIsDelivery,
        setIsDeliveryAddressInvalid,
        setIsPickup,
        setIsPickupInvalid,
        setSelectedStoreId,
        setStoreAddress,
        setStores,
        singleStore,
        storeAddress,
        stores,
        externalStores,
        showOverlay,
        setShowOverlay,
        isDeliveryFromEconStore: !!delivery_details?.delivery,
        title,
        buttonLabel,
        pickupSectionTitle,
        deliverySectionTitle,
        mainButtonType,
        pickupMainButtonText,
        deliveryMainButtonText,
        secondaryButtonType,
        secondaryButtonText,
        secondaryButtonLink,
        hideSecondaryButton,
        hideIconSecondaryButton,
        showSearchBox,
        pickupAllStore,
      }}
    >
      <div
        className={classNames(style.store_locator__container, {
          [style.store_locator__container__centered_single_row]:
            activeSlice?.variation === 'withCenteredLogoAndLocatorFix',
        })}
      >
        {children}
      </div>
    </StoreLocatorContext>
  );
};

export { StoreLocatorProvider };
